import type { components } from '@/api/generated/schema'

export type RestoreKind = components['schemas']['RestoreKind']
export type RestorePoint = components['schemas']['RestorePoint']
export type RestorePlan = components['schemas']['RestorePlan']
export type RestoreConfirmation = components['schemas']['RestoreConfirmation']
export type RestoreObjectCatalogue = components['schemas']['RestoreObjectCatalogue']
export type CatalogueKind = RestoreObjectCatalogue['kinds'][number]
export type CatalogueObject = CatalogueKind['objects'][number]
export type JobRef = components['schemas']['JobRef']
export type RestoreInput = components['schemas']['RestoreInput']
export type PrincipalMap = RestoreInput['principal_map']

/**
 * `process/09-SCREEN-SPECS/_family-wizard.md` — the eight restore machines' shared engine.
 * **Every pane's role (`select`/`destination`/`plan`/`order`) is asserted from the pane's own
 * heading, never typed** (`E-D01`) — this file is the one place that assertion is made concrete as
 * a reducer, the same relationship `layers/onboarding/state.ts` has to `ob-wz-1`…`6`.
 *
 * **Restructured (2026-09-16): `select` and `repositories` are two panes, not one — a UX call, not
 * a fidelity fix, and `Prototype_v2.html` itself was changed to match.** `select` (restore point
 * only) → `repositories` (repositories/projects, when the kind's `target_scope` form calls for
 * them — absent for `WORKSPACE`-form kinds, which have nothing to list) → `destination` (object
 * types from the catalogue, plus the destination workspace) → `plan` → `order`. Six of the eight
 * machines are five panes; `wshook` (`WORKSPACE` form) skips `repositories` and stays four; `ref` is
 * six, because its `repositories` role itself splits further: pick the one repository (`WORKSPACE
 * `_family-wizard.md`'s D2 rule to `narrow to exactly one` on this machine only), then pick the ref
 * itself with a presence probe (`selectRef`) — everything downstream is identical to the other seven.
 */
export interface RestoreWizardConfig {
  readonly kind: RestoreKind
  readonly kindLabel: string
  readonly unitIds: {
    readonly select: string
    /** Absent only for `WORKSPACE`-form kinds (`wshook`) — nothing to list, so no pane exists. */
    readonly repositories?: string
    readonly selectRef?: string
    readonly targets: string
    readonly plan: string
    readonly order: string
  }
  /** `RestoreObjectCatalogue.kinds[].target_scope` names this per kind — served, not guessed — but
   * the `select` pane needs to know which picker to open before that call resolves, so it is
   * threaded through config rather than awaited twice. */
  readonly targetScopeForm: 'REPOSITORIES' | 'PROJECTS' | 'WORKSPACE'
  /** `ref` narrows `REPOSITORIES` to exactly one and needs the extra `selectRef` pane — the one
   * structural difference from the other seven machines. */
  readonly singleRef: boolean
  /** `review`/`wshook` pick object INSTANCES (which pull requests, which hooks) rather than object
   * types. `RestoreInput` has no field narrower than `repository_ids`/`object_types` for either —
   * confirmed against the contract, not assumed — so their `targets` pane lists and probes real
   * instances for the operator to review, but the restore itself still covers everything the
   * snapshot captured for the chosen repositories. Reported as a gap, not built around silently. */
  readonly instances?: {
    readonly noun: string
    readonly probeCapability: 'ref' | 'pull-request-branches' | 'workspace-hook'
  }
}

export type WizardStage =
  | { readonly stage: 'select' }
  | { readonly stage: 'repositories'; readonly restorePoint: RestorePoint }
  | { readonly stage: 'selectRef'; readonly restorePoint: RestorePoint; readonly repositoryId: string; readonly repositoryLabel: string }
  | {
      readonly stage: 'targets'
      readonly restorePoint: RestorePoint
      readonly repositoryIds?: readonly string[]
      readonly projectKeys?: readonly string[]
      readonly repositoryId?: string
      readonly ref?: string
    }
  | {
      readonly stage: 'plan'
      readonly restorePoint: RestorePoint
      readonly repositoryIds?: readonly string[]
      readonly projectKeys?: readonly string[]
      readonly repositoryId?: string
      readonly ref?: string
      readonly objectTypes?: readonly string[]
      readonly destinationWorkspaceId?: string
      readonly destinationLabel: string
      readonly principalMap?: PrincipalMap
    }
  | {
      readonly stage: 'order'
      readonly restorePoint: RestorePoint
      readonly repositoryIds?: readonly string[]
      readonly projectKeys?: readonly string[]
      readonly repositoryId?: string
      readonly ref?: string
      readonly objectTypes?: readonly string[]
      readonly destinationWorkspaceId?: string
      readonly destinationLabel: string
      readonly principalMap?: PrincipalMap
    }

export type WizardAction =
  | { readonly type: 'SELECT_COMPLETE'; readonly restorePoint: RestorePoint }
  | { readonly type: 'REPOSITORIES_COMPLETE'; readonly repositoryIds?: readonly string[]; readonly projectKeys?: readonly string[]; readonly repositoryId?: string; readonly repositoryLabel?: string }
  | { readonly type: 'REF_PICKED'; readonly ref: string }
  | { readonly type: 'TARGETS_PICKED'; readonly objectTypes?: readonly string[]; readonly destinationWorkspaceId?: string; readonly destinationLabel: string; readonly principalMap?: PrincipalMap }
  | { readonly type: 'PLAN_ACCEPTED' }
  | { readonly type: 'BACK' }
  // `v-remap`'s own "Continue" hands the wizard back its 'plan' stage verbatim, principal map
  // filled in — the round trip a cross-workspace destination needs (W155).
  | { readonly type: 'RESUME_AT_PLAN'; readonly stage: Extract<WizardStage, { stage: 'plan' }> }

export function wizardReducer(config: RestoreWizardConfig, state: WizardStage, action: WizardAction): WizardStage {
  switch (action.type) {
    case 'SELECT_COMPLETE':
      if (config.targetScopeForm === 'WORKSPACE') return { stage: 'targets', restorePoint: action.restorePoint }
      return { stage: 'repositories', restorePoint: action.restorePoint }
    case 'REPOSITORIES_COMPLETE':
      if (state.stage !== 'repositories') return state
      if (config.singleRef) return { stage: 'selectRef', restorePoint: state.restorePoint, repositoryId: action.repositoryId ?? '', repositoryLabel: action.repositoryLabel ?? '' }
      return { stage: 'targets', restorePoint: state.restorePoint, repositoryIds: action.repositoryIds, projectKeys: action.projectKeys }
    case 'REF_PICKED':
      return state.stage === 'selectRef' ? { stage: 'targets', restorePoint: state.restorePoint, repositoryId: state.repositoryId, ref: action.ref } : state
    case 'TARGETS_PICKED':
      return 'restorePoint' in state
        ? {
            stage: 'plan',
            restorePoint: state.restorePoint,
            repositoryIds: 'repositoryIds' in state ? state.repositoryIds : undefined,
            projectKeys: 'projectKeys' in state ? state.projectKeys : undefined,
            repositoryId: 'repositoryId' in state ? state.repositoryId : undefined,
            ref: 'ref' in state ? state.ref : undefined,
            objectTypes: action.objectTypes,
            destinationWorkspaceId: action.destinationWorkspaceId,
            destinationLabel: action.destinationLabel,
            principalMap: action.principalMap,
          }
        : state
    case 'PLAN_ACCEPTED':
      return state.stage === 'plan' ? { ...state, stage: 'order' } : state
    case 'BACK':
      if (state.stage === 'repositories') return { stage: 'select' }
      if (state.stage === 'selectRef') return { stage: 'repositories', restorePoint: state.restorePoint }
      if (state.stage === 'targets') {
        if (config.targetScopeForm === 'WORKSPACE') return { stage: 'select' }
        if (config.singleRef && state.repositoryId) return { stage: 'selectRef', restorePoint: state.restorePoint, repositoryId: state.repositoryId, repositoryLabel: '' }
        return { stage: 'repositories', restorePoint: state.restorePoint }
      }
      if (state.stage === 'plan') return { stage: 'targets', restorePoint: state.restorePoint, repositoryIds: state.repositoryIds, projectKeys: state.projectKeys, repositoryId: state.repositoryId, ref: state.ref }
      if (state.stage === 'order') return { ...state, stage: 'plan' }
      return state
    case 'RESUME_AT_PLAN':
      return action.stage
    default:
      return state
  }
}

export const initialWizardStage: WizardStage = { stage: 'select' }
